/**
 * React Query hook for Practice Areas with Pagination Support
 * Designed for use with react-select-async-paginate
 * Provides automatic caching, background sync, and proper pagination handling
 */

import { useQuery } from '@tanstack/react-query';
import { getPracticeAreas } from 'services/CaseServices';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches practice areas with pagination tokens
 *
 * @param {Object} options - Query options
 * @param {number} options.pageSize - Number of items per page (default: 20)
 * @param {string} options.status - Filter by status (default: 'active')
 * @param {string} options.pagination_token - Token for pagination
 * @param {string} options.sort_order - Sort order (default: 'alphabet_ascending')
 * @param {string} options.searchQuery - Search query for filtering
 * @returns {Promise<Object>} - { options, hasMore, additional: { nextToken } }
 */
const fetchPracticeAreasPage = async ({
  pageSize = 20,
  status = 'active',
  pagination_token = '',
  sort_order = 'alphabet_ascending',
  searchQuery = '',
}) => {
  try {
    const response = await getPracticeAreas(
      null, // tenantId - will be retrieved internally
      null, // tenantSk - will be retrieved internally
      pageSize,
      pagination_token,
      'forward',
      { status },
      true, // excludeFromLoading
      sort_order
    );

    const practiceAreas = response?.data?.['practice-area'] || [];
    const nextToken = response?.data?.next_token || null;

    // Filter by search query if provided
    const filteredAreas = searchQuery
      ? practiceAreas.filter((pa) => pa.practice_area_name?.toLowerCase().includes(searchQuery.toLowerCase()))
      : practiceAreas;

    // Transform to react-select format
    const options = filteredAreas.map((pa) => ({
      label: pa.practice_area_name,
      value: pa.practice_area_id,
      ...pa, // Include full object for additional data access
    }));

    return {
      options,
      hasMore: nextToken !== null && nextToken !== undefined && nextToken !== '',
      additional: {
        nextToken: nextToken || '',
      },
    };
  } catch (error) {
    console.error('Error fetching practice areas page:', error);
    throw error;
  }
};

/**
 * Hook to fetch paginated practice areas
 * Compatible with react-select-async-paginate
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.searchQuery - Search query for filtering
 * @param {string} params.pagination_token - Token for current page
 * @param {number} params.pageSize - Number of items per page (default: 20)
 * @param {string} params.status - Filter by status (default: 'active')
 * @param {string} params.sort_order - Sort order (default: 'alphabet_ascending')
 * @param {boolean} params.enabled - Enable/disable query (default: true)
 * @returns {Object} - { data, isLoading, error, refetch }
 */
export const usePracticeAreasPaginated = ({
  searchQuery = '',
  pagination_token = '',
  pageSize = 20,
  status = 'active',
  sort_order = 'alphabet_ascending',
  enabled = true,
} = {}) => {
  const queryKey = queryKeys.practiceAreas.paginated({
    searchQuery,
    pagination_token,
    pageSize,
    status,
    sort_order,
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      fetchPracticeAreasPage({
        pageSize,
        status,
        pagination_token,
        sort_order,
        searchQuery,
      }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });

  return {
    data: data || { options: [], hasMore: false, additional: { nextToken: '' } },
    isLoading,
    error,
    refetch,
  };
};

/**
 * Load options function for react-select-async-paginate
 * This is a standalone function that can be used directly with AsyncPaginate
 *
 * @param {string} search - Search query
 * @param {Array} loadedOptions - Previously loaded options
 * @param {Object} additional - Additional data (pagination token)
 * @param {Object} config - Configuration options
 * @returns {Promise<Object>} - { options, hasMore, additional }
 */
export const loadPracticeAreaOptions = async (search, loadedOptions, additional, config = {}) => {
  const { pageSize = 20, status = 'active', sort_order = 'alphabet_ascending' } = config;

  try {
    const pagination_token = additional?.nextToken || '';

    const result = await fetchPracticeAreasPage({
      pageSize,
      status,
      pagination_token,
      sort_order,
      searchQuery: search,
    });

    return result;
  } catch (error) {
    console.error('Error loading practice area options:', error);
    return {
      options: [],
      hasMore: false,
      additional: { nextToken: '' },
    };
  }
};
